export const RECEIPT_ATTRIBUTE = 'Gift receipt';

// Only this attribute is owned here. Never submit notes, lines, prices or stock.
export function createReceiptStore(fetcher, root = '/') {
  let state = { selected: false, pending: false, error: false, ready: false };
  let desired = false;
  let active = null;
  const listeners = new Set();
  const emit = () => listeners.forEach((listener) => listener({ ...state }));
  const request = (write, value) => {
    if (active) return active;
    if (write) desired = value;
    state = { ...state, pending: true, error: false };
    emit();
    active = (async () => {
      try {
        const response = await fetcher(`${root}cart${write ? '/update' : ''}.js`, {
          method: write ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          signal: AbortSignal.timeout(15000),
          ...(write ? { body: JSON.stringify({ attributes: { [RECEIPT_ATTRIBUTE]: value ? 'Yes' : '' } }) } : {}),
        });
        if (!response.ok) throw new Error('Cart request failed');
        const cart = await response.json();
        if (!cart || !cart.attributes || typeof cart.attributes !== 'object' || Array.isArray(cart.attributes)) {
          throw new Error('Invalid cart response');
        }
        const selected = cart.attributes[RECEIPT_ATTRIBUTE] === 'Yes';
        if (write && selected !== value) throw new Error('Preference not confirmed');
        desired = selected;
        state = { selected, ready: true, error: false, pending: false };
      } catch {
        // A failed response can follow a committed write. Do not claim success or
        // allow checkout until a retry confirms the requested preference.
        state = { ...state, pending: false, error: true };
      } finally {
        active = null;
        emit();
      }
    })();
    return active;
  };
  return {
    subscribe(listener) { listeners.add(listener); listener({ ...state }); return () => listeners.delete(listener); },
    load: () => request(false),
    set: (value) => request(true, Boolean(value)),
    retry: () => state.ready ? request(true, desired) : request(false),
    snapshot: () => ({ ...state }),
  };
}

if (typeof document !== 'undefined' && !customElements.get('reel-gift-receipt')) {
  const store = createReceiptStore(window.fetch.bind(window), window.Shopify?.routes?.root || '/');
  // Restore only gates we own. Never enable a checkout disabled by the theme.
  const gated = new Map();
  const gateCheckout = (blocked) => {
    blocked = blocked || document.documentElement.dataset?.reelWrappingBlocked === 'true';
    document.querySelectorAll('.cart__ctas').forEach((element) => {
      if (blocked && !gated.has(element)) {
        gated.set(element, element.inert);
        element.inert = true;
      }
    });
    if (!blocked) {
      gated.forEach((wasInert, element) => { element.inert = wasInert; });
      gated.clear();
    }
  };
  store.subscribe((state) => gateCheckout(state.pending || state.error || !state.ready));
  document.addEventListener('reel:wrapping-state', () => {
    const state = store.snapshot();
    gateCheckout(state.pending || state.error || !state.ready);
    document.querySelectorAll('reel-gift-receipt').forEach((element) => element.sync?.());
  });
  new MutationObserver(() => {
    // Horizon can replace children without reconnecting their custom-element
    // parent. Rebind controls and restore live state after that replacement.
    document.querySelectorAll('reel-gift-receipt').forEach((element) => element.sync?.());
    const state = store.snapshot();
    gateCheckout(state.pending || state.error || !state.ready);
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('submit', (event) => {
    if (event.target?.id !== 'cart-form') return;
    const state = store.snapshot();
    if (state.pending || state.error || !state.ready || document.documentElement.dataset?.reelWrappingBlocked === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('reel-gift-receipt [data-status]')?.focus();
    }
  }, true);
  class GiftReceipt extends HTMLElement {
    connectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = store.subscribe(() => this.sync());
      this.sync();
    }
    sync() {
      const checkbox = this.querySelector('input');
      const retry = this.querySelector('button');
      const status = this.querySelector('[data-status]');
      if (!checkbox || !retry || !status) return;
      if (this.checkbox !== checkbox || this.retryButton !== retry) {
        this.abort?.abort();
        this.abort = new AbortController();
        this.checkbox = checkbox;
        this.retryButton = retry;
        checkbox.addEventListener('change', () => store.set(checkbox.checked), { signal: this.abort.signal });
        retry.addEventListener('click', () => store.retry(), { signal: this.abort.signal });
      }
      const state = store.snapshot();
        checkbox.checked = state.selected;
        checkbox.disabled = state.pending || !state.ready || document.documentElement.dataset?.reelWrappingBlocked === 'true';
        retry.hidden = !state.error;
        retry.disabled = state.pending;
        const text = state.error ? this.dataset.error : state.pending ? this.dataset.saving :
          state.selected ? this.dataset.selected : this.dataset.unselected;
        // Avoid a MutationObserver feedback loop caused by rewriting text nodes.
        if (status.textContent !== text) status.textContent = text;
        if (this.getAttribute('aria-busy') !== String(state.pending)) this.setAttribute('aria-busy', String(state.pending));
        gateCheckout(state.pending || state.error || !state.ready);
      if (!this.started) {
        this.started = true;
        store.load();
      }
    }
    disconnectedCallback() { this.unsubscribe?.(); this.abort?.abort(); this.checkbox = null; this.started = false; }
  }
  customElements.define('reel-gift-receipt', GiftReceipt);
  window.addEventListener('pageshow', (event) => { if (event.persisted) store.load(); });
}
